import React, { useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiEdit2, FiMail, FiUserX, FiUserCheck, FiShield, FiGrid } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";
import { UserStatusConfirmModal } from "../UserStatusConfirmModal";
import { useResendInviteMutation } from "../hooks/useResendInviteMutation";
import { useReactivateUserMutation } from "../hooks/useReactivateUserMutation";
import { useCurrentUserId } from "hooks/auth";
import type { UserListItem } from "../../../types/userInvite";

interface UserRowActionsProps {
  user: UserListItem;
  /*
   * PRO-709: false for a user whose `settings` module is view-only. Every action
   * here mutates (edit, resend, deactivate, reactivate) or opens an
   * administrative RBAC surface that 403s for a viewer ("Manage roles",
   * "View access"), so the whole cluster is withheld rather than rendered and
   * then refused. Defaults true so existing call sites are unchanged.
   */
  canManageUsers?: boolean;
  onDeactivate: (user: UserListItem) => void;
  onEdit: (user: UserListItem) => void;
  // PRO-413: RBAC v2 additive layer — "Manage roles" opens ManageRolesModal.
  rbacEnabled?: boolean;
  onManageRoles?: (user: UserListItem) => void;
  // PRO-413 #UM1: "View access" opens the read-only effective-access matrix.
  onViewAccess?: (user: UserListItem) => void;
}

interface ActionButtonProps {
  colorClass?: string;
  "data-testid"?: string;
  disabled?: boolean;
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
}

const ActionButton = ({
  onClick,
  icon,
  title,
  colorClass = "text-gray-500 hover:text-gray-700",
  disabled = false,
  ...rest
}: ActionButtonProps) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    title={title}
    disabled={disabled}
    className={`rounded p-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${colorClass}`}
    {...rest}
  >
    {icon}
  </button>
);

type ConfirmAction = "resend" | "reactivate";

export const UserRowActions = ({
  user,
  onEdit,
  onDeactivate,
  canManageUsers = true,
  rbacEnabled = false,
  onManageRoles,
  onViewAccess,
}: UserRowActionsProps) => {
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const currentUserId = useCurrentUserId();

  const resend = useResendInviteMutation();
  const reactivate = useReactivateUserMutation();

  const isPending = resend.isPending || reactivate.isPending;

  const isOwnUser = user.id === currentUserId;
  const canResend = user.status === "invited";
  const canDeactivate = !isOwnUser && (user.status === "active" || user.status === "invited");
  const canReactivate = user.status === "deactivated";

  /*
   * An administrator may not administer their OWN record (product ruling). The
   * server refuses it on every write — profile edit, role assignment, scope,
   * flags and the 2FA reset — so the affordances are disabled here rather than
   * offered and then 403'd. Deactivate already worked this way; the rest did not.
   *
   * Disabled, not hidden: an admin looking for their own row should find it and
   * be told why it is inert, and the title says where to go instead. "View
   * access" stays live — reading your own matrix has never been the issue.
   */
  const SELF_ADMIN_HINT = "You cannot edit your own account here. Use My Profile instead.";

  // Negative IDs are tour-fixture dummy users — never fire real mutations.
  const isDummy = user.id < 0;

  const handleConfirm = () => {
    if (isDummy) {
      setConfirmAction(null);
      return;
    }
    if (confirmAction === "resend") {
      resend.mutate(
        { userId: user.id },
        { onSettled: () => setConfirmAction(null) }
      );
    } else if (confirmAction === "reactivate") {
      reactivate.mutate(user.id, {
        onSettled: () => setConfirmAction(null),
      });
    }
  };


  if (!canManageUsers) {
    return null;
  }

  return (
    <>
      <ReadOnlyGuard>
      <div className="flex items-center justify-end gap-1">
        {rbacEnabled && onManageRoles && (
          <ActionButton
            onClick={() => onManageRoles(user)}
            icon={<FiShield size={14} />}
            title={isOwnUser ? SELF_ADMIN_HINT : "Manage roles"}
            colorClass="text-gray-500 hover:text-blue-600"
            data-testid={`user-action-manage-roles-${user.id}`}
            disabled={isPending || isOwnUser}
          />
        )}
        {rbacEnabled && onViewAccess && (
          <ActionButton
            onClick={() => onViewAccess(user)}
            icon={<FiGrid size={14} />}
            title="View access"
            colorClass="text-gray-500 hover:text-blue-600"
            data-testid={`user-action-view-access-${user.id}`}
            disabled={isPending}
          />
        )}
        <ActionButton
          onClick={() => onEdit(user)}
          icon={<FiEdit2 size={14} />}
          title={isOwnUser ? SELF_ADMIN_HINT : "Edit user"}
          data-testid={`user-action-edit-${user.id}`}
          disabled={isPending || isOwnUser}
        />
        {canResend && (
          <ActionButton
            onClick={() => setConfirmAction("resend")}
            icon={isPending && confirmAction === "resend"
              ? <Spinner size="xs" />
              : <FiMail size={14} />}
            title="Resend invite"
            data-testid={`user-action-resend-${user.id}`}
            disabled={isPending}
          />
        )}
        {canDeactivate && (
          <ActionButton
            onClick={() => onDeactivate(user)}
            icon={<FiUserX size={14} />}
            title="Deactivate user"
            colorClass="text-red-500 hover:text-red-700"
            data-testid={`user-action-deactivate-${user.id}`}
            disabled={isPending}
          />
        )}
        {canReactivate && (
          <ActionButton
            onClick={() => setConfirmAction("reactivate")}
            icon={isPending && confirmAction === "reactivate"
              ? <Spinner size="xs" />
              : <FiUserCheck size={14} />}
            title="Reactivate user"
            colorClass="text-gray-500 hover:text-green-600"
            data-testid={`user-action-reactivate-${user.id}`}
            disabled={isPending}
          />
        )}
      </div>
      </ReadOnlyGuard>

      {confirmAction === "resend" && (
        <Modal
          isOpen
          modalType="alert"
          iconType="mail"
          title="Resend invite?"
          description={`A new invite will be sent to ${user.email}. The invite link expires in 72 hours.`}
          primaryButton={{
            text: "Resend invite",
            isLoading: isPending,
            disabled: isPending,
            onClick: handleConfirm,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setConfirmAction(null),
          }}
          onClose={() => setConfirmAction(null)}
        >
          <></>
        </Modal>
      )}

      {confirmAction === "reactivate" && (
        <UserStatusConfirmModal
          isOpen
          action="reactivate"
          firstName={user.first_name}
          isLoading={isPending}
          onConfirm={handleConfirm}
          onClose={() => setConfirmAction(null)}
        />
      )}
    </>
  );
};
